import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faChevronDown, faMessage, faSearch } from '@fortawesome/free-solid-svg-icons';
import Banner from './Banner';
import '../styles/FAQ.css';

const uiCopy = {
  fr: {
    search: 'Rechercher : tarif, fauteuil, accompagnement…', count: 'questions', empty: 'Aucune question ne correspond à votre recherche.',
    categories: { all: 'Toutes', booking: 'Réservation & horaires', prices: 'Tarifs', services: 'Services & véhicules', practical: 'Informations pratiques' }
  },
  nl: {
    search: 'Zoeken: tarief, rolstoel, begeleiding…', count: 'vragen', empty: 'Geen enkele vraag komt overeen met uw zoekopdracht.',
    categories: { all: 'Alle', booking: 'Reservatie & uren', prices: 'Tarieven', services: 'Diensten & voertuigen', practical: 'Praktische informatie' }
  }
};

const categoryByIndex = ['booking', 'prices', 'prices', 'services', 'services', 'services', 'services', 'practical', 'practical'];

const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const renderAnswer = (answer) => answer
  .trim()
  .split(/\n\s*\n/)
  .filter(Boolean)
  .map((block, blockIndex) => {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
    const bullets = lines.filter((line) => line.startsWith('- '));
    const paragraphs = lines.filter((line) => !line.startsWith('- '));

    return (
      <React.Fragment key={`${blockIndex}-${block.slice(0, 18)}`}>
        {paragraphs.length > 0 && (
          <div className={paragraphs.length > 1 ? 'faq-answer-lines' : ''}>
            {paragraphs.map((paragraph) => (
              <p key={paragraph} className={paragraph.endsWith(':') ? 'faq-answer-label' : ''}>{paragraph}</p>
            ))}
          </div>
        )}
        {bullets.length > 0 && (
          <ul>
            {bullets.map((bullet) => <li key={bullet}>{bullet.slice(2)}</li>)}
          </ul>
        )}
      </React.Fragment>
    );
  });

const FAQ = () => {
  const { t, i18n } = useTranslation();
  const reducedMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const lang = i18n.resolvedLanguage?.startsWith('nl') ? 'nl' : 'fr';
  const c = uiCopy[lang];
  const contactCopy = lang === 'fr'
    ? {
        eyebrow: 'Une autre question ?',
        title: 'Vous n’avez pas trouvé votre réponse ?',
        text: 'Décrivez-nous votre situation. Notre équipe vous répondra avec une information claire et adaptée à votre trajet.',
        action: 'Accéder au formulaire'
      }
    : {
        eyebrow: 'Nog een vraag?',
        title: 'Uw antwoord niet gevonden?',
        text: 'Beschrijf uw situatie. Ons team bezorgt u duidelijke informatie die bij uw verplaatsing past.',
        action: 'Naar het formulier'
      };

  const faqData = useMemo(() => Array.from({ length: 9 }, (_, index) => ({
    id: index,
    category: categoryByIndex[index],
    question: t(`translate.faq_q${index + 1}_title`),
    answer: t(`translate.faq_q${index + 1}_answer`)
  })), [t]);

  const filteredFaq = faqData.filter((item) => {
    const matchesCategory = category === 'all' || item.category === category;
    const haystack = normalize(`${item.question} ${item.answer}`);
    return matchesCategory && haystack.includes(normalize(query.trim()));
  });

  return (
    <div className="faq-page-modern">
      <Banner
        title={t('translate.faq_title')}
        accent={lang === 'fr' ? 'questions' : 'vragen'}
        subtitle={t('translate.faq_subtitle')}
      />

      <div className="faq-container">
        <div className="faq-tools">
          <div className="faq-search-row">
            <label className="faq-search">
              <FontAwesomeIcon icon={faSearch} />
              <span className="sr-only">{c.search}</span>
              <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={c.search} />
            </label>
            <p className="faq-count"><strong>{filteredFaq.length}</strong><span>{c.count}</span></p>
          </div>
          <div className="faq-filters" role="tablist" aria-label={lang === 'fr' ? 'Catégories de questions' : 'Vraagcategorieën'}>
            {Object.entries(c.categories).map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={category === key} className={category === key ? 'active' : ''} onClick={() => setCategory(key)}>{label}</button>
            ))}
          </div>
        </div>

        <div className="faq-list">
          {filteredFaq.map((item) => {
            const isActive = activeIndex === item.id;
            return (
              <motion.article
                key={item.id}
                className={`faq-item ${isActive ? 'active' : ''}`}
                initial={reducedMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.28, delay: Math.min(item.id * 0.025, 0.15) }}
                layout={!reducedMotion}
              >
                <button className={`faq-question ${isActive ? 'active' : ''}`} onClick={() => setActiveIndex(isActive ? null : item.id)} aria-expanded={isActive} aria-controls={`faq-answer-${item.id}`}>
                  <span className="faq-question-copy">
                    <small><strong>{String(item.id + 1).padStart(2, '0')}</strong>{c.categories[item.category]}</small>
                    <span>{item.question}</span>
                  </span>
                  <span className="faq-toggle-icon" aria-hidden="true"><FontAwesomeIcon icon={faChevronDown} /></span>
                </button>
                <AnimatePresence initial={false}>
                  {isActive && (
                    <motion.div
                      id={`faq-answer-${item.id}`}
                      className="faq-answer"
                      initial={reducedMotion ? false : { height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: reducedMotion ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="faq-answer-inner">
                        <span className="faq-answer-marker" aria-hidden="true"><FontAwesomeIcon icon={faCheck} /></span>
                        <div className="faq-answer-content">{renderAnswer(item.answer)}</div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.article>
            );
          })}
          {filteredFaq.length === 0 && <p className="faq-empty">{c.empty}</p>}
        </div>

        <motion.aside
          className="faq-contact-cta"
          initial={reducedMotion ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.5 }}
        >
          <span className="faq-contact-icon"><FontAwesomeIcon icon={faMessage} /></span>
          <div className="faq-contact-copy">
            <span>{contactCopy.eyebrow}</span>
            <h2>{contactCopy.title}</h2>
            <p>{contactCopy.text}</p>
          </div>
          <Link to="/contact#contact-form" className="faq-contact-link">
            {contactCopy.action}<FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </motion.aside>
      </div>
    </div>
  );
};

export default FAQ;
